/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Tooltip } from 'antd'
import { Link } from 'react-router-dom'
import { ROUTES } from '@sportninja/common/constants/app'
import colors from '@sportninja/common/constants/appColors'
import type { RecentGameCardModel } from '@sportninja/common/features/profile/stats/types'
import { MATCHUP_PREFIX } from '@sportninja/common/features/profile/stats/utils/gameResult'
import { getStatName } from '@sportninja/common/features/profile/stats/utils/statNames'

import StatusChip from 'src/components/ts-components/StatusChip'
import Text from 'src/components/ts-components/Text'

import {
  NEUTRAL_RESULT_TONE,
  RESULT_TONES,
  TEAM_PLACEHOLDER,
} from '../constants'

interface RecentGameCardProps {
  game: RecentGameCardModel
  /** Abbr → full stat name, shown on hover of each stat label. */
  statNames: Record<string, string>
}

/** One recent game: date, result, opponent and three key stats. */
const RecentGameCard = ({ game, statNames }: RecentGameCardProps) => {
  'use memo'

  const tone = game.result.outcome
    ? RESULT_TONES[game.result.outcome]
    : NEUTRAL_RESULT_TONE

  return (
    <Link to={`${ROUTES.GAME_ROOT}/${game.gameId}`} css={cardStyles}>
      <div css={topRowStyles}>
        <Text variant='paragraphXSmall' color={colors.NEUTRAL_50}>
          {game.date}
        </Text>
        <StatusChip tone={tone}>{game.result.label}</StatusChip>
      </div>
      {/* "vs {logo} {team}"; the logo is decorative, so the text reads as one. */}
      <div css={opponentStyles}>
        <Text
          variant='paragraphSmall'
          weight='medium'
          color={colors.NEUTRAL_50}
          style={PREFIX_TEXT_STYLE}
        >
          {MATCHUP_PREFIX}
        </Text>
        <img
          src={game.opponentLogo ?? TEAM_PLACEHOLDER}
          alt=''
          css={logoStyles}
        />
        <Text
          variant='paragraphSmall'
          weight='medium'
          color={colors.WHITE}
          style={NAME_TEXT_STYLE}
        >
          {game.opponentName}
        </Text>
      </div>
      <div css={statsStyles}>
        {game.stats.map((stat) => (
          <div key={stat.abbr} css={statStyles}>
            <Text variant='heading6' weight='semiBold' color={colors.WHITE}>
              {stat.value}
            </Text>
            <Tooltip title={getStatName(statNames, stat.abbr)}>
              <div>
                <Text variant='paragraphXSmall' color={colors.NEUTRAL_50}>
                  {stat.abbr}
                </Text>
              </div>
            </Tooltip>
          </div>
        ))}
      </div>
    </Link>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

const cardStyles = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  border: 1px solid ${colors.SECONDARY_300};
  border-radius: 8px;
  background: ${colors.SECONDARY_400};
  text-decoration: none;
  transition: border-color 0.15s ease-in-out;

  &:hover,
  &:focus-visible {
    border-color: ${colors.PRIMARY};
  }
`

const topRowStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const opponentStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`

// `Text` takes extra rules as a CSS string (it has no className).
const PREFIX_TEXT_STYLE = 'flex-shrink: 0;'

/** A long team name truncates; "vs" and the logo always show. */
const NAME_TEXT_STYLE = `
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const logoStyles = css`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 8px;
  object-fit: contain;
`

const statsStyles = css`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
`

const statStyles = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`

export default RecentGameCard
